import { createContext, useContext, useState, useEffect } from 'react';
import { organizationsApi } from '../api/organizations';
import { subscriptionsApi } from '../api/subscriptions';
import { s3Api } from '../api/s3';
import { retryApiCall } from '../api/apiClient';
import * as directSubscriptionsService from '../services/directSubscriptions';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
	const [userData, setUserData] = useState(null);
	const [orgData, setOrgData] = useState(null);
	const [adminOrgs, setAdminOrgs] = useState([]);
	const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
	const [storageUsage, setStorageUsage] = useState(null);
	const [storageLimit, setStorageLimit] = useState(0);
	const [isLoadingStorage, setIsLoadingStorage] = useState(false);
	const [subscription, setSubscription] = useState(null);

	useEffect(() => {
		const storedUser = localStorage.getItem('userData');
		const storedOrg = localStorage.getItem('orgData');
		const storedAdminOrgs = localStorage.getItem('adminOrgs');
		const storedToken = localStorage.getItem('authToken');

		// Only proceed if we have both user data and a token
		if (storedUser && storedToken) {
			try {
				const parsedUser = JSON.parse(storedUser);
				if (parsedUser) {
					setUserData(parsedUser);
				}

				if (storedAdminOrgs) {
					const parsedAdminOrgs = JSON.parse(storedAdminOrgs);
					if (parsedAdminOrgs) {
						setAdminOrgs(parsedAdminOrgs);

						// Set orgData from storage or default to first admin org
						const storedOrgData = storedOrg
							? JSON.parse(storedOrg)
							: null;
						if (storedOrgData) {
							setOrgData(storedOrgData);
						} else if (parsedAdminOrgs.length > 0) {
							setOrgData(parsedAdminOrgs[0]);
							localStorage.setItem(
								'orgData',
								JSON.stringify(parsedAdminOrgs[0])
							);
						}
					}
				}

				// Load subscription status
				const storedSubscriptionStatus = localStorage.getItem(
					'hasActiveSubscription'
				);
				if (storedSubscriptionStatus) {
					setHasActiveSubscription(
						JSON.parse(storedSubscriptionStatus)
					);
				}

				// Load storage limit
				const storedStorageLimit = localStorage.getItem('storageLimit');
				if (storedStorageLimit) {
					setStorageLimit(JSON.parse(storedStorageLimit));
				}
			} catch (error) {
				console.error('Error parsing stored auth data:', error);
				localStorage.removeItem('userData');
				localStorage.removeItem('orgData');
				localStorage.removeItem('adminOrgs');
				localStorage.removeItem('authToken');
			}
		}
	}, []);

	const fetchSubscriptionStatus = async (organizationId) => {
		try {
			const response = await subscriptionsApi.getOrganizationSubscription(
				organizationId
			);
			return response?.status === 'active';
		} catch (error) {
			console.error('Error fetching subscription status:', error);
			return false;
		}
	};

	const fetchStorageUsage = async (organizationId) => {
		if (!organizationId) return null;

		try {
			setIsLoadingStorage(true);
			const usage = await s3Api.getFolderSize(organizationId, '');
			setStorageUsage(usage);
			return usage;
		} catch (error) {
			console.error('Error fetching storage usage:', error);
			return null;
		} finally {
			setIsLoadingStorage(false);
		}
	};

	const calculateStorageLimit = (subscriptionData) => {
		if (!subscriptionData) {
			return 1; // Default to 1GB if no subscription data
		}

		// Base storage from the plan
		const plan = subscriptionData.plan || {};
		const baseStorage = plan.storageGB || 1; // Default to 1GB if not specified

		// Additional storage from add-ons
		const items = subscriptionData.items || [];
		const storageAddons = items.filter(
			(item) => item.type === 'storage-addon'
		);

		const addonStorage = storageAddons.length * 50; // 50GB per add-on

		return baseStorage + addonStorage;
	};

	const login = async (userData) => {
		try {
			if (!userData?.user) {
				console.error('No user data in login payload');
				return;
			}

			// Ensure we have a token
			if (!userData.token) {
				console.error('No token in login payload');
				// Create a temporary token for testing if needed
				// userData.token = `Bearer TEMP_${btoa(`${userData.user.id}:${Date.now()}`)}`;
				// console.warn('Created temporary token for testing');
				return;
			}

			// Ensure token has Bearer prefix
			const token = userData.token.startsWith('Bearer ')
				? userData.token
				: `Bearer ${userData.token}`;

			// Store token first to ensure it's available for subsequent API calls
			localStorage.setItem('authToken', token);
			// Store user data
			setUserData(userData.user);
			localStorage.setItem('userData', JSON.stringify(userData.user));

			// Store admin organizations if they exist
			if (userData.organizations?.adminOrgs) {
				setAdminOrgs(userData.organizations.adminOrgs);
				localStorage.setItem(
					'adminOrgs',
					JSON.stringify(userData.organizations.adminOrgs)
				);

				// Set default organization to the first admin org
				if (userData.organizations.adminOrgs.length > 0) {
					const defaultOrg = userData.organizations.adminOrgs[0];
					setOrgData(defaultOrg);
					localStorage.setItem('orgData', JSON.stringify(defaultOrg));

					// Add a small delay to ensure token is properly stored before making API calls
					await new Promise((resolve) => setTimeout(resolve, 100));

					// Fetch subscription status for the organization
					let hasActiveSubscription = false;
					let subscriptionData = null;

					try {
						// Use the retry mechanism for critical API calls
						const response = await retryApiCall(() =>
							subscriptionsApi.getOrganizationSubscription(
								defaultOrg.id
							)
						);

						// The response itself is the subscription object
						subscriptionData = response;

						// Check if subscription exists and is active
						hasActiveSubscription = response?.status === 'active';

						// Calculate and store storage limit
						if (subscriptionData) {
							const limit =
								calculateStorageLimit(subscriptionData);
							setStorageLimit(limit);
							localStorage.setItem(
								'storageLimit',
								JSON.stringify(limit)
							);
						}
					} catch (error) {
						console.error(
							'Error fetching subscription data after retries:',
							error
						);
						// Continue despite errors
					}

					// Store subscription status in state
					setHasActiveSubscription(hasActiveSubscription);

					// Store in localStorage
					localStorage.setItem(
						'hasActiveSubscription',
						JSON.stringify(hasActiveSubscription)
					);
				}
			}

			// Store the token in localStorage
			if (userData.token) {
				localStorage.setItem('token', userData.token);
			}

			// Try to fetch subscription data directly
			if (userData.organizations?.adminOrgs?.length > 0) {
				try {
					const subscriptionData =
						await directSubscriptionsService.getOrganizationSubscription(
							userData.organizations.adminOrgs[0].id
						);
					setSubscription(subscriptionData);
				} catch (directError) {
					console.error(
						'Direct subscription fetch failed:',
						directError
					);

					// Fall back to regular API if direct fails
					try {
						const subscriptionData =
							await subscriptionsApi.getOrganizationSubscription(
								userData.organizations.adminOrgs[0].id
							);
						setSubscription(subscriptionData);
					} catch (apiError) {
						console.error(
							'Regular API subscription fetch failed:',
							apiError
						);
						// Continue without subscription data
						setSubscription(null);
					}
				}
			}
		} catch (error) {
			console.error('Error during login:', error);
		}
	};

	const switchOrganization = (org) => {
		setOrgData(org);
		localStorage.setItem('orgData', JSON.stringify(org));
	};

	const logout = () => {
		// Clear state
		setUserData(null);
		setOrgData(null);
		setAdminOrgs([]);
		setHasActiveSubscription(false);
		setStorageLimit(0);

		// Clear localStorage
		localStorage.removeItem('userData');
		localStorage.removeItem('orgData');
		localStorage.removeItem('adminOrgs');
		localStorage.removeItem('authToken');
		localStorage.removeItem('hasActiveSubscription');
		localStorage.removeItem('storageLimit');

		// Redirect to login page
		window.location.href = '/dashboard/login';
	};

	const refreshOrgData = async (orgId) => {
		try {
			const updatedOrg = await organizationsApi.getOne(orgId);
			setOrgData(updatedOrg);
			localStorage.setItem('orgData', JSON.stringify(updatedOrg));
			return updatedOrg;
		} catch (error) {
			console.error('Error refreshing organization data:', error);
			throw error;
		}
	};

	const checkStorageLimit = async (fileSize, organizationId = null) => {
		const orgId = organizationId || orgData?.id;
		if (!orgId)
			return { canUpload: false, message: 'No organization selected' };

		// Refresh storage usage
		const usage = await fetchStorageUsage(orgId);
		if (!usage)
			return {
				canUpload: false,
				message: 'Unable to fetch storage usage',
			};

		// Convert file size to GB if it's in bytes
		const fileSizeGB = fileSize / (1024 * 1024 * 1024);

		// Check if upload would exceed limit
		const currentUsageGB = usage.gigabytes || 0;
		const totalAfterUpload = currentUsageGB + fileSizeGB;

		if (totalAfterUpload > storageLimit) {
			return {
				canUpload: false,
				message: `Upload would exceed your storage limit of ${storageLimit} GB. Current usage: ${currentUsageGB.toFixed(
					2
				)} GB, Upload size: ${fileSizeGB.toFixed(2)} GB`,
				currentUsage: currentUsageGB,
				uploadSize: fileSizeGB,
				limit: storageLimit,
			};
		}

		return {
			canUpload: true,
			currentUsage: currentUsageGB,
			uploadSize: fileSizeGB,
			limit: storageLimit,
		};
	};

	const value = {
		userData,
		setUserData,
		orgData,
		setOrgData,
		adminOrgs,
		setAdminOrgs,
		switchOrganization,
		login,
		logout,
		refreshOrgData,
		hasActiveSubscription,
		setHasActiveSubscription,
		storageUsage,
		storageLimit,
		isLoadingStorage,
		fetchStorageUsage,
		checkStorageLimit,
		calculateStorageLimit,
		subscription,
		setSubscription,
	};

	return (
		<AuthContext.Provider value={value}>{children}</AuthContext.Provider>
	);
};

export const useAuth = () => useContext(AuthContext);
